import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import {
    CustomerBillingPriceArticleModel,
    IBillingPriceArticleLabel,
    ICustomerBillingPriceModel,
} from '../customer-billing-price.model';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AppTextService } from '@fi-shared/app-text';
import { CustomerBillingPriceService } from '../customer-billing-price.service';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { SideListModel } from '@fi-app/utilities/side-list/side-list.model';
import { FiSideListType } from '@fi-app/utilities/side-list/side-list-type';
import { IPackageType, IPackageTypeMap, PackageType, PackageTypes } from '@fi-app/billing/package-type';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { IPricePerType, PricePerTypes } from '../price-per-type';
import { BillingModuleTypes } from '@fi-shared/models/module-types';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { isDefined } from '@fi-shared/utils';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { CurrencyTypes, ICurrencyType } from '@fi-app/customer-billing/customer-billing-price/currency-type';
import {
    FiToggleSwitchLabelAlign,
    FiToggleSwitchSliderColorClass,
} from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { IContentLanguage } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { createMap } from '@fi-app/shared/utils/create-map';

@Component({
    selector: 'fi-customer-billing-price-edit',
    templateUrl: './customer-billing-price-edit.component.html',
    styleUrls: ['./customer-billing-price-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-customer-billing-price-edit' },
    standalone: false,
})
export class CustomerBillingPriceEditComponent implements OnInit {
    @Input() priceArticle: CustomerBillingPriceArticleModel;

    EditTabs: EditTabsModel;
    ccmSideList: SideListModel;
    moreMenuItems: MoreMenuMobileSettingsModel;

    PackageTypesMap: IPackageTypeMap = createMap(PackageTypes, 'Id');
    pricePerTypesDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();
    priceModuleIdDropdownSettings = new DropdownSettingsModelV2();
    ActiveItem: number;
    FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;

    private defaultContentLanguage: IContentLanguage;

    constructor(
        private appTextService: AppTextService,
        private priceService: CustomerBillingPriceService,
        private contentLanguagesService: ContentLanguagesService,
        private fiLoadingService: LoadingService,
        private stateService: FiStateService,
        private objectTitleService: ObjectTitleService,
        private userDataService: FiUserDataService,
        private detectDevicesService: DetectDevicesService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        /* if (!this.priceArticle.Id) {
            /*this.priceArticle.Prices.push({
                AllowedUsage: 0,
                UsageLevel: PackageType.Free,
                PriceValues: [],
            })
        }*/

        this.setPageHeaderData();
        this.setInitialData();
    }

    private setPageHeaderData() {
        this.defaultContentLanguage = this.contentLanguagesService.GetDefaultLanguage();

        this.EditTabs = new EditTabsModel(this.getPageTitle());
        this.EditTabs.Tabs.push(new EditTabModel('', ''));

        this.moreMenuItems = new MoreMenuMobileSettingsModel();
        this.moreMenuItems.AddListItem(this.appTextService.get('common.select_lang'), () => {
            this.openSelectLanguageDialog();
        });
    }

    private openSelectLanguageDialog() {
        this.contentLanguagesService.openSelectLanguageDialog(this.ActiveItem).onYesButtonClick((result: number) => {
            this.ActiveItem = result;
            this.cdr.detectChanges();
        });
    }

    private setInitialData() {
        this.ccmSideList = new SideListModel(
            FiSideListType.Translations,
            this.userDataService.applicationLanguage,
            this.userDataService.userData.PreferredContentLanguage,
        );

        const existingLabelsLanguages: number[] = this.priceArticle.Labels.map((label: IBillingPriceArticleLabel) => {
            return label.Language;
        });
        this.priceArticle.Labels.push(
            ...this.contentLanguagesService
                .GetContentLanguages()
                .filter((lang: IContentLanguage) => existingLabelsLanguages.indexOf(lang.Id) === -1)
                .map((lang: IContentLanguage) => ({
                    Language: lang.Id,
                    Name: '',
                    Description: '',
                })),
        );

        const existingTypes: PackageType[] = this.priceArticle.Prices.map((item: ICustomerBillingPriceModel) => {
            return item.UsageLevel;
        });
        PackageTypes.filter((type: IPackageType) => existingTypes.indexOf(type.Id) === -1).forEach(
            (type: IPackageType) => {
                const newPrice: ICustomerBillingPriceModel = {
                    UsageLevel: type.Id,
                    AllowedUsage: undefined,
                    PriceValues: CurrencyTypes.map((currencyType: ICurrencyType) => ({
                        CurrencyType: currencyType.Id,
                        Price: 0,
                    })),
                };
                this.priceArticle.Prices.splice(type.Id - 1, 0, newPrice);
            },
        );

        this.pricePerTypesDropdownSettings.orderBy = 'Id';
        this.pricePerTypesDropdownSettings.setDataSet(
            PricePerTypes.map((item: IPricePerType) => {
                return {
                    Id: item.Id,
                    Name: this.appTextService.get(item.TextId).toLowerCase(),
                };
            }),
        );
        if (!isDefined(this.priceArticle.PricePer))
            this.priceArticle.PricePer = this.pricePerTypesDropdownSettings.dataSet[0].Id;

        this.priceModuleIdDropdownSettings.orderBy = 'Id';

        if (this.isModuleRequired()) {
            const moduleTypesMap = createMap(BillingModuleTypes, 'Id');
            this.priceArticle.ModuleNameTextId = moduleTypesMap[this.priceArticle.ModuleId].TextId;
        }
    }

    getNamePlaceholder(label: IBillingPriceArticleLabel): string {
        return label.Language == this.defaultContentLanguage.Id
            ? this.appTextService.get('common.required')
            : this.getDefaultLanguageValue(this.priceArticle.Labels).Name;
    }

    getDescriptionPlaceholder(label: IBillingPriceArticleLabel): string {
        return label.Language == this.defaultContentLanguage.Id
            ? ''
            : this.getDefaultLanguageValue(this.priceArticle.Labels).Description;
    }

    private getDefaultLanguageValue(labels: IBillingPriceArticleLabel[]): IBillingPriceArticleLabel {
        return labels.find((label: IBillingPriceArticleLabel) => label.Language === this.defaultContentLanguage.Id);
    }

    isNameRequired(label: IBillingPriceArticleLabel): boolean {
        return label.Language == this.defaultContentLanguage.Id;
    }

    isModuleRequired(): boolean {
        return this.priceArticle.IsModule;
    }

    getPageTitle(): string {
        if (this.priceArticle.Id) {
            const defaultArticleLabel: IBillingPriceArticleLabel = this.priceArticle.Labels.find(
                (label: IBillingPriceArticleLabel) => label.Language === this.defaultContentLanguage.Id,
            );

            if (isDefined(defaultArticleLabel)) return this.objectTitleService.getEditTitle(defaultArticleLabel.Name);
        }

        return this.objectTitleService.getCreateTitle(ObjectTypes.BillingPriceArticle);
    }

    save() {
        this.fiLoadingService.trackPromise(this.priceService.SaveBillingPrice(this.priceArticle)).then(() => {
            this.stateService.go(FiStateNames.CustomerBilling.Price.List);
        });
    }

    getModuleIdText(): string {
        return `${this.priceArticle.ModuleId} - ${this.appTextService.get(this.priceArticle.ModuleNameTextId)}`;
    }

    isLabelShown(label: IBillingPriceArticleLabel): boolean {
        return label.Language === this.ActiveItem;
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
}
